import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Text,
  Group,
  ActionIcon,
  Image,
  Tabs,
  Tooltip,
} from "@mantine/core";
import iconCheck from "../images/check.png";
import iconSetting from "../images/settings.png";
import CustomTabs from "../common/CustomTabs";
import SearchBar from "../common/SearchBar";
import NotificationListPersonal from "../list/NotificationListPersonal";
import NotificationListPromotion from "../list/NotificationListPromotion";
import { NotificationFilterState } from "../types/type";
import { NOTIFICATION_DETAIL } from "../constants/drawer.constant";
import DrawerLayout from "./DrawerLayout";
import { useMarkAllAsReadMutation } from "@/services/notificationAPI";
import { NsType } from "@/dto/notifications/response/enum";

interface DrawerProps {
  activeDrawer: "notifications" | "notifications-detail" | "filter" | null;
  setActiveDrawer: any;
  filterState: NotificationFilterState;
  setFilterState: any;
}

// Main Notifications Drawer
export const NotificationsDrawer = ({
  activeDrawer,
  setActiveDrawer,
  filterState,
  setFilterState,
}: DrawerProps) => {
  const { t } = useTranslation();
  const [markAllAsRead, { isLoading: isMarkingAll }] = useMarkAllAsReadMutation();

  // Incrementing this key forces NotificationListPersonal to remount after
  // mark-all-as-read, resetting its pageNo + allApiItems state from scratch.
  const [listKey, setListKey] = useState(0);

  const handleMarkAllAsRead = async () => {
    const tab = filterState.status === "news" ? NsType.News : NsType.Private;
    await markAllAsRead({ nsType: tab });
    setListKey((k) => k + 1);
  };

  const handleSearch = (keyword: string) => {
    setFilterState((prev: any) => ({ ...prev, keyword }));
  };

  const title = (
    <Box
      sx={{
        padding: "0 20px",
        display: "flex",
        justifyContent: "space-between",
        height: "64px",
        alignItems: "center",
        borderBottom: "1px solid #F8F9FB",
      }}
    >
      <Text weight={700} size={18}>
        {t("NOTIFICATIONS")}
      </Text>
      <Group spacing={10}>
        <Tooltip label={t("MARK_ALL_AS_READ")} withArrow>
          <ActionIcon
            size="sm"
            variant="transparent"
            loading={isMarkingAll}
            onClick={handleMarkAllAsRead}
          >
            <Image
              src={iconCheck}
              alt="Mark all as read"
              style={{ width: 24, height: 24 }}
            />
          </ActionIcon>
        </Tooltip>
        <ActionIcon size="sm" variant="transparent">
          <Image
            src={iconSetting}
            alt="Settings"
            style={{ width: 24, height: 24 }}
          />
        </ActionIcon>
      </Group>
    </Box>
  );

  const content = (
    <Tabs
      value={filterState.status}
      onTabChange={(value) =>
        setFilterState((prev: any) => ({ ...prev, status: value }))
      }
      keepMounted={false}
    >
      <CustomTabs
        tabs={filterState.status}
        onTabChange={(value) =>
          setFilterState((prev: any) => ({ ...prev, status: value }))
        }
      />

      <Tabs.Panel value="personal">
        <SearchBar
          key="Personal"
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
        />
        <NotificationListPersonal
          key={`${listKey}-${filterState.types}-${filterState.dateRange.from?.getTime() ?? 0}-${filterState.dateRange.to?.getTime() ?? 0}`}
          selectedType={filterState.types}
          fromDate={filterState.dateRange.from}
          toDate={filterState.dateRange.to}
          setActiveDrawer={setActiveDrawer}
        />
      </Tabs.Panel>

      <Tabs.Panel value="news" pt="xs">
        <SearchBar
          activeDrawer={activeDrawer}
          setActiveDrawer={setActiveDrawer}
          onSearch={handleSearch}
          defaultValue={filterState.keyword}
        />
        <NotificationListPromotion
          openNotificationDetail={() => setActiveDrawer(NOTIFICATION_DETAIL)}
          setActiveDrawer={setActiveDrawer}
        />
      </Tabs.Panel>
    </Tabs>
  );

  return (
    <DrawerLayout
      title={title}
      content={content}
      opened={activeDrawer === "notifications"}
      onClose={() => setActiveDrawer(null)}
    />
  );
};

export default NotificationsDrawer;
